import { useEffect, useRef, useState, useCallback } from 'react';

interface UseScrollRevealReturn<T extends HTMLElement> {
  ref: React.RefObject<T | null>;
  isVisible: boolean;
}

export function useScrollReveal<T extends HTMLElement>(threshold = 0.15): UseScrollRevealReturn<T> {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

// Hook avec stagger pour les listes d'enfants
export function useStaggerReveal<T extends HTMLElement>(
  itemCount: number,
  threshold = 0.1,
  staggerDelay = 100
) {
  const ref = useRef<T | null>(null);
  const [visibleItems, setVisibleItems] = useState<boolean[]>(Array(itemCount).fill(false));

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setVisibleItems(Array(itemCount).fill(true));
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          for (let i = 0; i < itemCount; i++) {
            setTimeout(() => {
              setVisibleItems((prev) => {
                const next = [...prev];
                if (i < next.length) next[i] = true;
                return next;
              });
            }, i * staggerDelay);
          }
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin: '0px 0px -30px 0px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [itemCount, threshold, staggerDelay]);

  return { ref, visibleItems };
}

// Utilitaire pour la classe CSS d'animation
export function getRevealClass(
  isVisible: boolean,
  options?: {
    duration?: number;
    delay?: number;
    direction?: 'up' | 'down' | 'left' | 'right';
    distance?: number;
  }
): string {
  const {
    duration = 700,
    delay = 0,
    direction = 'up',
    distance = 24,
  } = options || {};

  const dirMap: Record<string, string> = {
    up: `translateY(${isVisible ? 0 : distance}px)`,
    down: `translateY(${isVisible ? 0 : -distance}px)`,
    left: `translateX(${isVisible ? 0 : distance}px)`,
    right: `translateX(${isVisible ? 0 : -distance}px)`,
  };

  const willChange = 'will-change';
  const opacity = isVisible ? 1 : 0;
  const transform = dirMap[direction] || dirMap.up;

  return `${willChange}: transform, opacity; transition: opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms; opacity: ${opacity}; transform: ${transform} translateZ(0);`;
}

export function useRevealStyle(
  isVisible: boolean,
  options?: {
    duration?: number;
    delay?: number;
    direction?: 'up' | 'down' | 'left' | 'right';
    distance?: number;
  }
) {
  const {
    duration = 700,
    delay = 0,
    direction = 'up',
    distance = 24,
  } = options || {};

  const dirMap: Record<string, [number, number]> = {
    up: [0, distance],
    down: [0, -distance],
    left: [distance, 0],
    right: [-distance, 0],
  };

  const [tx, ty] = isVisible ? [0, 0] : (dirMap[direction] || [0, distance]);

  return useCallback(
    () => ({
      opacity: isVisible ? 1 : 0,
      transform: `translate3d(${tx}px, ${ty}px, 0)`,
      transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms`,
      willChange: 'transform, opacity',
    }),
    [isVisible, tx, ty, duration, delay]
  )();
}